<template>
  <div class="min-h-screen bg-primary w-full h-full mt-[60px] md:mt-0 themeTransition">
    <div v-if="!isLoading">
      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <!-- content begin -->
        <hero v-if="hero" v-bind="{ ...hero }" />
        <div id="content" class="col-span-full" v-if="sections && sections.length > 0">
          <component
            v-for="(section, index) in sections"
            :key="index"
            :is="getComponent(section?.name as ComponentType)"
            :ref="section?.name"
            v-bind="{ ...section?.props }"
          ></component>
        </div>
        <!-- content end -->
      </page-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed } from "vue";
import { IFrontPage } from "@/types/IFrontPage";
import { changeTheme, parseUrl } from "@/util/helper";
import { getPage, getPageByID } from "@/util/api";
import { Theme } from "@/types/types";
import Hero, { HeroProps } from "@molecules/Hero/Hero.ce.vue";
import PageWrapper from "@components/PageWrapper.vue";
import { getComponentData, getLink, getStroke, getComponent } from "@/util/getComponent";
import { ComponentType } from "@/util/constants";

const isLoading = ref(true);
const pageData = ref<IFrontPage | null>(null);
const theme = ref<Theme | null>(null);
const pageGUI = ref<string | undefined>();

const urlParams = ref<String[] | null>(null);
const environment = ref();
const isPreview = ref<boolean>(false);
const startUrl = ref<string | null>(null);
const pageKey = ref<string | undefined>();

onBeforeMount(async () => {
  environment.value = parseUrl(window.location.href).environment;
  urlParams.value = parseUrl(window.location.href).params;
  pageKey.value = document.documentElement.dataset.id;

  //TBD CHANGE WITH URLS
  startUrl.value = window.location.pathname;
  if (urlParams.value.length > 0) {
    isPreview.value = true;
  }

  const data = await getPageByID(pageKey.value, isPreview.value);

  //const data = await getPage("/home", isPreview.value);
  if (data) {
    pageData.value = data as IFrontPage;
    theme.value = pageData.value.properties.backgroundColor;
    if (theme.value) changeTheme(theme.value);
    pageGUI.value = pageData.value.route.startItem.id;
    isLoading.value = false;
  }
});

const pageProperties = computed(() => {
  return pageData.value?.properties;
});

const heroStroke = computed(() => {
  if (!pageProperties.value || !theme.value) return;
  const stroke = {
    lightSelected: pageProperties.value.lightSelectedHero,
    oceanSelected: pageProperties.value.oceanSelectedHero,
    aubergineSelected: pageProperties.value.aubergineSelectedHero,
    grassSelected: pageProperties.value.grassSelectedHero,
  };
  return getStroke(theme.value, stroke);
});

const hero = computed(() => {
  if (!pageProperties.value) return;
  const link = getLink(pageProperties.value.headerLink);

  return {
    theme: theme.value,
    stroke: heroStroke.value && heroStroke.value,
    strokePos: pageProperties.value.strokePos ? "right" : "left",
    title: pageProperties.value.title,
    subtitle: pageProperties.value.tag,
    text: pageProperties.value.description.markup,
    image: JSON.stringify(pageProperties.value.heroImage[0]),
    imgAlt: pageProperties.value.imageAltText,
    ctaLabel: link?.title,
    ctaLink: link?.url,
  } as HeroProps;
});

const sections = computed(() => {
  if (!pageData.value?.properties.grid) return [];

  const sections = pageData.value?.properties.grid.items.map((item) => {
    const { content } = item;
    if (!content) return;
    return getComponentData(content);
  });
  const sanitizedSections = sections.filter(Boolean);
  return sanitizedSections;
});
</script>
